import React, { BaseSyntheticEvent, useEffect } from 'react';
import { ModalProps } from '../../../types/modal';
import {
    Avatar,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
    IconButton,
    InputLabel,
    ListItemAvatar,
    ListItemText,
    Stack,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import {
    useCreateMessageTemplateMutation,
    useGetIndividualOwnerAssetsQuery,
    useUpdateMessageTemplateMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../shared/AuthRoles';
import { FormValues as Experience } from '../../../types/experience';
import { Close } from '@mui/icons-material';
import FormTextField from '../../../shared/FormTextField';
import FormMultiAutocomplete from '../../../shared/FormMultiAutocomplete';
import MessageField from './MessageField';
import { MessageTemplate } from '../../../types/messaging';
import { z } from 'zod';
import toast from '../../../shared/Toast';
import { zodResolver } from '@hookform/resolvers/zod';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import { MessageTemplateEvent } from '../../../enums/messaging';

const Schema = z.object({
    name: z.string().min(1, { message: 'Required' }),
    message: z.string().min(1, { message: 'Required' }),
    modules: z
        .array(z.number())
        .transform((arr) => arr.map((value) => ({ id: value }))),
    event: z.nativeEnum(MessageTemplateEvent, {
        message: 'Required',
    }),
    interval: z.number().min(-7).max(7),
});

interface ScheduledMessagesModalProps extends ModalProps {
    template: MessageTemplate | null;
}

const ScheduledMessagesModal = ({
    template,
    open,
    onClose,
}: ScheduledMessagesModalProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const loggedInUser = UserRole();

    const methods = useForm<MessageTemplate>({
        resolver: zodResolver(Schema),
        defaultValues: {
            name: '',
            message: '',
            modules: [],
            interval: 0,
            event: MessageTemplateEvent.Approved,
        },
    });
    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { isDirty },
    } = methods;

    const { data: experiences } = useGetIndividualOwnerAssetsQuery({
        url: `owners/${loggedInUser?.company}/modules`,
        status: 'published',
    });

    const [createMessageTemplate] = useCreateMessageTemplateMutation();
    const [updateMessageTemplate] = useUpdateMessageTemplateMutation();

    const event = useWatch({ control, name: 'event' });

    const onSubmit = async (data: MessageTemplate, e?: BaseSyntheticEvent) => {
        try {
            const event = e?.nativeEvent as any;
            const value = event?.submitter?.value;

            const request = {
                ...data,
                isDraft: value === 'draft',
            };

            if (template?.id) {
                await updateMessageTemplate([template.id, request]).unwrap();
            } else {
                await createMessageTemplate(request).unwrap();
            }

            toast.success('Message template saved successfully');
            handleClose();
        } catch (error) {
            console.error(error);
            toast.error('Error saving message templates');
        }
    };

    useEffect(() => {
        if (template) {
            const { modules, ...rest } = template;

            reset({
                ...rest,
                // @ts-expect-error transform from number to { id }
                modules: modules.map(({ id }) => id),
            });
        }
    }, [template]);

    useEffect(() => {
        if (!event || !isDirty) return;

        if (event === MessageTemplateEvent.Checkin) {
            setValue('interval', -1);
            return;
        }

        if (event === MessageTemplateEvent.Completed) {
            setValue('interval', 1);
            return;
        }

        setValue('interval', 0);
    }, [event]);

    const handleClose = () => {
        reset({
            name: '',
            message: '',
            modules: [],
            isDraft: false,
            event: MessageTemplateEvent.Approved,
        });
        onClose();
    };

    const events = [
        {
            desc: 'Sent when a pending reservation is approved or when an instant booking is made',
            value: MessageTemplateEvent.Approved,
            label: 'Confirmation',
        },
        {
            desc: 'Sent when a reservation is cancelled',
            value: MessageTemplateEvent.Cancelled,
            label: 'Cancellation',
        },
        {
            desc: "Sent at a custom time relative to a reservation's check-in date",
            value: MessageTemplateEvent.Checkin,
            label: 'Check-in',
        },
        {
            desc: "Sent at a custom time relative to a reservation's checkout date",
            value: MessageTemplateEvent.Completed,
            label: 'Checkout',
        },
    ];

    const intervals = [
        ...(event === MessageTemplateEvent.Checkin
            ? [
                  { value: -7, label: '1 Week Before' },
                  { value: -6, label: '6 Days Before' },
                  {
                      value: -5,
                      label: '5 Days Before',
                  },
                  {
                      value: -4,
                      label: '4 Days Before',
                  },
                  {
                      value: -3,
                      label: '3 Days Before',
                  },
                  {
                      value: -2,
                      label: '2 Days Before',
                  },
              ]
            : []),
        {
            value: -1,
            label: '1 Day Before',
        },
        {
            value: 0,
            label: 'Same Day',
        },
        {
            value: 1,
            label: '1 Day After',
        },
        ...(event === MessageTemplateEvent.Completed
            ? [
                  {
                      value: 2,
                      label: '2 Days After',
                  },
                  {
                      value: 3,
                      label: '3 Days After',
                  },
                  {
                      value: 4,
                      label: '4 Days After',
                  },
                  {
                      value: 5,
                      label: '5 Days After',
                  },
                  { value: 6, label: '6 Days After' },
                  { value: 7, label: '1 Week After' },
              ]
            : []),
    ];

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullScreen={isMobile}
            fullWidth
            maxWidth='sm'
        >
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogTitle>
                        Message Template
                        <IconButton
                            aria-label='close'
                            onClick={onClose}
                            size='small'
                            sx={{ color: 'text.primary' }}
                        >
                            <Close />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={2}>
                            <div>
                                <InputLabel
                                    required
                                    htmlFor='template-name__input'
                                >
                                    Template Name
                                </InputLabel>
                                <FormTextField
                                    id='template-name__input'
                                    name='name'
                                    control={control}
                                    placeholder='Confirmation Message'
                                />
                                <FormHelperText>
                                    This won't be shown to guests
                                </FormHelperText>
                            </div>
                            <div>
                                <InputLabel required htmlFor='message__input'>
                                    Message
                                </InputLabel>
                                <MessageField />
                            </div>
                            <div>
                                <InputLabel htmlFor='experiences__input'>
                                    Experiences
                                </InputLabel>
                                <FormMultiAutocomplete
                                    id='experiences__input'
                                    name='modules'
                                    control={control}
                                    options={
                                        experiences?.map(
                                            (experience: Experience) => ({
                                                value: experience.id,
                                                label: experience.name,
                                                ...experience,
                                            }),
                                        ) || []
                                    }
                                    renderOption={(
                                        props,
                                        option: Experience,
                                    ) => (
                                        <li key={option.id} {...props}>
                                            <ListItemAvatar>
                                                <Avatar
                                                    variant='rounded'
                                                    src={
                                                        option.photos?.[0]?.src
                                                    }
                                                />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={option.name}
                                                secondary={`${option.streetAddress}, ${option.suburb}`}
                                            />
                                        </li>
                                    )}
                                />
                                <FormHelperText>
                                    Select experiences this template will apply
                                    to. If none are selected, all experiences
                                    will use this template.
                                </FormHelperText>
                            </div>
                            <div>
                                <InputLabel htmlFor='event__input'>
                                    Scheduled for
                                </InputLabel>
                                <FormAutocomplete
                                    id='event__input'
                                    name='event'
                                    control={control}
                                    options={events}
                                    renderOption={(
                                        props,
                                        option: (typeof events)[0],
                                    ) => (
                                        <li key={option.value} {...props}>
                                            <ListItemText
                                                primary={option.label}
                                                secondary={option.desc}
                                            />
                                        </li>
                                    )}
                                />
                                {(event === MessageTemplateEvent.Checkin ||
                                    event ===
                                        MessageTemplateEvent.Completed) && (
                                    <>
                                        <FormAutocomplete
                                            id='interval__input'
                                            name='interval'
                                            control={control}
                                            shrinkLabel={false}
                                            options={intervals}
                                        />
                                        <FormHelperText>
                                            Messages will be sent at 9AM on the
                                            selected day
                                        </FormHelperText>
                                    </>
                                )}
                            </div>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button variant='outlined' type='submit' value='draft'>
                            Save as Draft
                        </Button>
                        <Button variant='contained' type='submit'>
                            Save
                        </Button>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};

export default ScheduledMessagesModal;
